// @flow

import React, { Fragment } from 'react';
import type { Node } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { Link } from '@riseart/common';
import { FilterService } from '@riseart/filter-service';
import { Section, Wrapper } from '@riseart/layout';
import { ArtDetails, ArtDetailsItem } from '@riseart/art';
import { filter as FILTER_ENUM, meta as META_ENUM } from 'Enum';
import { MetaProvider } from 'shared_data/providers/meta/Meta';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { Avatar } from 'shared_components/common/avatar/Avatar';
import { NotFoundContent } from 'shared_components/common/notfound/Content';
import { ArtistBreadcrumbs } from 'shared_components/artist/breadcrumbs/Breadcrumbs';
import { ArtistBirthText } from 'shared_components/artist/bio/BirthText';
import { ArtistLivesText } from 'shared_components/artist/bio/LivesText';
import { MENU_ITEMS } from 'shared_services/riseart/utils/artist';
import { LDTYPES, generateLDJSON } from 'shared_models/seo/ldJSON';
import { getFilterDomainConfig } from 'shared_models/configs/filters/domains';
import { notFoundArtistCls } from 'shared_components/common/notfound/Content.less';

type Props = {
  data: Object,
  metaSubscriber: string,
};

/**
 * NotFoundArtist
 *
 * @param {Props} props
 * @returns {Node}
 */
export function NotFoundArtist({ data, metaSubscriber }: Props): Node {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const { name } = data;

  if (!data) {
    return null;
  }

  const filterService = new FilterService(
    getFilterDomainConfig(FILTER_ENUM.domain.ARTIST),
    formatMessage,
  );
  const canonicalURL = UrlAssembler.artistProfile(data.id, data.alias, {
    origin: LocationManager.get('origin'),
  });

  return (
    <Fragment>
      <MetaProvider
        metaSubscriber={metaSubscriber}
        meta={{
          [META_ENUM.METATYPE.LINK_CANONICAL]: canonicalURL,
          [META_ENUM.METATYPE.LINK_CANONICAL_REDIRECT]: UrlAssembler.artistProfile(
            data.id,
            data.alias,
            {
              ...(location.search ? { search: location.search } : null),
              ...(location.hash ? { hash: location.hash } : null),
            },
          ),
          [META_ENUM.METATYPE.META_TITLE]: formatMessage(
            { id: 'meta.artistProfile.dynamicMetaTitle' },
            { name },
          ),
          [META_ENUM.METATYPE.META_DESCRIPTION]: formatMessage(
            { id: 'meta.artistProfile.dynamicMetaDescription' },
            { name },
          ),
          [META_ENUM.METATYPE.SCRIPT_LD_JSON_ARTIST]: generateLDJSON(LDTYPES.ARTIST, {
            ...data,
            url: canonicalURL,
            mainEntityOfPage: {
              type: 'WebPage',
              url: canonicalURL,
            },
          }),
          [META_ENUM.METATYPE.PAGE_TITLE]: formatMessage({ id: 'meta.artist.pageTitle' }, { name }),
          [META_ENUM.METATYPE.META_ROBOTS]: META_ENUM.ATTRIBUTE_CONTENT.NO_INDEX,
        }}
      />
      <Section padding="none">
        <Wrapper>
          <ArtistBreadcrumbs
            pageLabel={MENU_ITEMS.profile}
            id={data.id}
            alias={data.alias}
            name={data.name}
            mediums={data.mediums}
            styles={data.styles}
          />
        </Wrapper>
      </Section>
      <NotFoundContent
        className={notFoundArtistCls}
        image={
          <Avatar
            size="large"
            align="center"
            imageObject={data.image ? { type: data.image.type, ...data.image.file } : null}
            name={data.name && data.name.toUpperCase()}
          />
        }
        title={data.name}
        content={
          (data.mediums && data.mediums.length) ||
          (data.styles && data.styles.length) ||
          data.birthCountry ||
          data.birthYear ||
          data.birthCity ||
          data.livesCountry ||
          data.livesCity ? (
            <ArtDetails nodeType="ul">
              {data.birthCountry || data.birthYear || data.birthCity ? (
                <ArtDetailsItem
                  key="birth"
                  separator=":&nbsp;&nbsp;"
                  name={formatMessage({ id: 'components.artist.bornInLabel' })}
                >
                  <ArtistBirthText
                    birthCountry={data.birthCountry}
                    birthCity={data.birthCity}
                    birthYear={data.birthYear}
                  />
                </ArtDetailsItem>
              ) : null}
              {data.livesCountry || data.livesCity ? (
                <ArtDetailsItem
                  key="lives"
                  separator=":&nbsp;&nbsp;"
                  name={formatMessage({ id: 'components.artist.livesLabel' })}
                >
                  <ArtistLivesText livesCountry={data.livesCountry} livesCity={data.livesCity} />
                </ArtDetailsItem>
              ) : null}
              {data.mediums && data.mediums.length ? (
                <ArtDetailsItem
                  key="medium"
                  separator=":&nbsp;&nbsp;"
                  name={formatMessage({ id: `components.filters.medium.label` })}
                >
                  {data.mediums.map((medium, idx) => {
                    const translation = formatMessage({ id: `filters.artist.medium.${medium}` });

                    return (
                      <Fragment key={medium}>
                        {idx ? ', ' : null}
                        <Link to={filterService.assemble({ medium }, {})} title={translation}>
                          {translation}
                        </Link>
                      </Fragment>
                    );
                  })}
                </ArtDetailsItem>
              ) : null}
              {data.styles && data.styles.length ? (
                <ArtDetailsItem
                  key="style"
                  separator=":&nbsp;&nbsp;"
                  name={formatMessage({ id: 'components.filters.style.label' })}
                >
                  {data.styles.map((style, idx) => {
                    const translation = formatMessage({ id: `filters.artist.style.${style}` });

                    return (
                      <Fragment key={style}>
                        {idx ? ', ' : null}
                        <Link to={filterService.assemble({ style }, {})} title={translation}>
                          {translation}
                        </Link>
                      </Fragment>
                    );
                  })}
                </ArtDetailsItem>
              ) : null}
            </ArtDetails>
          ) : null
        }
        message={<strong>{formatMessage({ id: 'components.artist.unavailable' })}</strong>}
      />
    </Fragment>
  );
}
